/**
 * Pure validation helpers for assessments and assessment instances.
 * Kept free of react-admin/MUI imports so they can be unit-tested directly
 * (importing the resource modules breaks vitest's ESM resolution).
 */

export const hasDuplicates = (arr: any[] = []): boolean =>
  new Set(arr).size !== arr.length;

/**
 * Returns the list of chosen (non-empty) event IDs from the events form value.
 * Shared by the picker component and the validator so both agree on what
 * "selected" means.
 */
export const selectedEventIds = (value: any): string[] =>
  (value ?? [])
    .map((e: any) => e?.event_id)
    .filter((id: any) => id !== undefined && id !== null && id !== '');

/**
 * Validates the assessment Events array:
 *   - Requires at least 2 chosen events (domain minimum).
 *   - Rejects duplicates.
 * Empty/unchosen rows do not count toward the minimum.
 */
export const validateAssessmentEvents = (value: any): string | undefined => {
  const chosen = selectedEventIds(value);

  if (chosen.length === 0)
    return 'An assessment requires at least 2 events — add your first event';
  if (chosen.length === 1)
    return 'An assessment requires at least 2 events — add one more';
  if (hasDuplicates(chosen))
    return 'Assessment cannot contain duplicate events';

  return undefined;
};

// ---------------------------------------------------------------------------
// Option-state logic for the add-event combobox
// ---------------------------------------------------------------------------

export type NoOptionsState = 'loading' | 'empty_catalog' | 'no_match' | 'hint';

/**
 * Returns the semantic state driving noOptionsText in the add-event combobox.
 *
 * @param isLoading       - true while the search request is in flight
 * @param totalInCatalog  - total events in the tenant (unfiltered); undefined = not yet loaded
 * @param searchQuery     - the current user-typed query string
 */
export function noOptionsState(
  isLoading: boolean,
  totalInCatalog: number | undefined,
  searchQuery: string,
): NoOptionsState {
  if (isLoading) return 'loading';
  if (totalInCatalog === 0) return 'empty_catalog';
  if (searchQuery.trim()) return 'no_match';
  return 'hint';
}

/**
 * Duplicate check for flight assignments. Rows where nothing has been picked
 * yet are ignored for the same empty-row reason as above.
 */
export const validateFlights = (value: any): string | undefined => {
  if (!value) return undefined;
  const flat = value
    ?.map((t: any) => [...(t?.instructors || []), ...(t?.teams || [])])
    .flat(3)
    .filter((id: any) => id !== undefined && id !== null && id !== '');

  if (hasDuplicates(flat)) {
    return 'Flights contain duplicate users';
  }
  return undefined;
};

export type AssessmentStatus = 'draft' | 'ready' | 'published';

const STATUS_CHIP_MAP: Record<AssessmentStatus, { label: string; color: 'default' | 'info' | 'success' }> = {
  draft:     { label: 'Draft',     color: 'default' },
  ready:     { label: 'Ready',     color: 'info'    },
  published: { label: 'Published', color: 'success' },
};

export function assessmentStatusChipProps(status: string | undefined): { label: string; color: 'default' | 'info' | 'success' } {
  return STATUS_CHIP_MAP[(status as AssessmentStatus) ?? 'draft'] ?? STATUS_CHIP_MAP['draft'];
}
